import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { FileText, Video, Sheet, ClipboardCheck, FileType, Star, Trash2, Pencil, Upload, Search, Play, Download, Image as ImageIcon } from 'lucide-react';

export const FORMATS = [
  { id: 'pdf', label: 'PDF', Icon: FileText },
  { id: 'doc', label: 'Word doc', Icon: FileType },
  { id: 'spreadsheet', label: 'Spreadsheet', Icon: Sheet },
  { id: 'checklist', label: 'Checklist', Icon: ClipboardCheck },
  { id: 'video', label: 'Video', Icon: Video },
  { id: 'image', label: 'Image', Icon: ImageIcon },
];
const formatMeta = (id) => FORMATS.find(f => f.id === id) || FORMATS[0];

export function ResourceCard({ r, role, resourceFileUrl, onEdit, onDelete }) {
  const { Icon, label } = formatMeta(r.format);
  const isVideo = r.format === 'video';
  const isImage = r.format === 'image';
  const link = r.videoUrl || (r.hasFile ? resourceFileUrl(r.id) : '');
  return (
    <div className="card span-4 resource-card" data-testid={`resource-card-${r.id}`}>
      {r.featured && <span className="pill yellow resource-featured-badge"><Star size={12} /> Featured</span>}
      {isImage && link ? <img src={link} alt={r.title} className="resource-image-preview" /> : <div className="resource-icon"><Icon size={24} /></div>}
      <div className="bc-badges" style={{ marginTop: 12 }}>
        <span className="pill soft">{r.sport}</span>
        <span className="pill soft">{label}</span>
        {r.topic && <span className="pill soft">{r.topic}</span>}
        {r.private && <span className="pill yellow" data-testid={`resource-visible-to-badge-${r.id}`}>🔒 {r.visibleTo === 'coach' ? 'Coaches only' : r.visibleTo === 'leader' ? 'Run Leaders only' : 'Leaders only'}</span>}
      </div>
      <h3 style={{ marginTop: 10 }}>{r.title}</h3>
      {r.description && <p>{r.description}</p>}
      <div className="meta">Added {r.createdAt} · {r.uploadedBy}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {link
          ? <a className="btn secondary" href={link} target="_blank" rel="noopener noreferrer" data-testid={`resource-open-${r.id}`}>{isVideo ? <><Play size={14} /> Watch</> : isImage ? <><ImageIcon size={14} /> View full size</> : <><Download size={14} /> {r.hasFile ? 'Download' : 'View'}</>}</a>
          : <span className="pill soft">No file attached</span>}
        {role === 'admin' && (
          <>
            <button className="btn ghost" onClick={() => onEdit(r)} data-testid={`resource-edit-${r.id}`}><Pencil size={13} /></button>
            <button className="btn danger" onClick={() => onDelete(r.id)} data-testid={`resource-delete-${r.id}`}><Trash2 size={13} /></button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Resources() {
  const { role, openModal, getResources, removeResource, resourceFileUrl, getResourceTopics } = useSpc();
  const [items, setItems] = React.useState(null);
  const [topics, setTopics] = React.useState([]);
  const [sport, setSport] = React.useState('All');
  const [format, setFormat] = React.useState('All');
  const [q, setQ] = React.useState('');

  const load = React.useCallback(async () => setItems(await getResources()), [getResources]);
  const loadTopics = React.useCallback(async () => setTopics(await getResourceTopics()), [getResourceTopics]);
  React.useEffect(() => { load(); loadTopics(); }, [load, loadTopics]);

  const list = items || [];
  const filtered = list.filter(r =>
    (sport === 'All' || r.sport === sport) &&
    (format === 'All' || r.format === format) &&
    (!q.trim() || `${r.title} ${r.description} ${r.topic}`.toLowerCase().includes(q.trim().toLowerCase()))
  );
  const featured = filtered.filter(r => r.featured);
  const rest = filtered.filter(r => !r.featured);

  const onEdit = (r) => openModal('editResource', { resource: r, onDone: load });
  const onDelete = async (id) => { if (window.confirm('Remove this resource?')) { await removeResource(id); load(); } };

  return (
    <>
      <div className="section-head">
        <div>
          <div className="eyebrow">Resource Library</div>
          <h2>Everything SPC, in one place</h2>
          <p>All your SPC guides, checklists, videos, replays and tools in one place. Filter by sport, topic or format and grab what you need, when you need it.</p>
        </div>
        {role === 'admin' && <button className="btn" onClick={() => openModal('newResource', { onDone: load })} data-testid="upload-resource-btn"><Upload size={15} /> Upload resource</button>}
      </div>
      <p className="resource-intro">This is your always-open training toolbox. You'll find PDFs, videos, spreadsheets and checklists covering swim, bike, run, S&amp;C, nutrition, mindset and tech &amp; data. New resources are added each month — keep an eye on the New &amp; Featured section at the top.</p>

      <div className="resource-filters" data-testid="resource-filters">
        <div className="resource-search-box"><Search size={15} /><input placeholder="Search resources…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="resource-search-input" /></div>
        <select value={sport} onChange={(e) => setSport(e.target.value)} data-testid="resource-sport-filter">
          <option value="All">All topics</option>
          {topics.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="resource-format-filter">
          <option value="All">All formats</option>
          {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      </div>

      {items === null ? (
        <div className="empty">Loading resources…</div>
      ) : !filtered.length ? (
        <div className="empty" data-testid="resources-empty">No resources match those filters yet.</div>
      ) : (
        <>
          {featured.length > 0 && (
            <>
              <div className="section-head" style={{ marginTop: 30 }}><div><h3>New &amp; Featured</h3></div></div>
              <div className="grid" data-testid="resources-featured-grid">
                {featured.map(r => <ResourceCard key={r.id} r={r} role={role} resourceFileUrl={resourceFileUrl} onEdit={onEdit} onDelete={onDelete} />)}
              </div>
            </>
          )}
          <div className="section-head" style={{ marginTop: 30 }}><div><h3>All resources</h3></div></div>
          <div className="grid" data-testid="resources-grid">
            {rest.map(r => <ResourceCard key={r.id} r={r} role={role} resourceFileUrl={resourceFileUrl} onEdit={onEdit} onDelete={onDelete} />)}
          </div>
        </>
      )}
    </>
  );
}
